import { useState, useMemo, useRef, useEffect } from 'react';
import {
  Autocomplete,
  AutocompleteRenderInputParams,
  Badge,
  Box,
  Chip,
  createFilterOptions,
  Dialog,
  DialogContent,
  ListItemButton,
  ListItemText,
  TextField,
} from '@mui/material';
import {
  useCommandPaletteDialogShortcuts,
  useRegisteredShortcuts,
} from 'hooks/useKeyboardShortcuts';
import { useTranslation } from 'react-i18next';
import { ForcedTheme } from 'components/ForcedTheme';
import { CommandPaletteActionCategory } from './commandPaletteAction.types';

const categoryOrder: Array<CommandPaletteActionCategory> = [
  'common',
  'baseMap',
  'mapLayers',
  'teamLayers',
  'mapTools',
  'camera',
];

type CommandOption = {
  groupBy: string;
  label: string;
  subtitle?: string;
  handler: () => void;
  render: (
    props: React.HTMLAttributes<HTMLLIElement>,
    key: React.Key,
  ) => JSX.Element;
};

// Match against both the label and the subtitle so searching a layer's description surfaces it too.
const filterCommandOptions = createFilterOptions<CommandOption>({
  stringify: (option) => `${option.label} ${option.subtitle ?? ''}`,
});

export const CommandPaletteDialog = (): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  //  Needed to set focus when this is first shown
  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    }
  }, [isOpen]);

  const { t } = useTranslation();
  const shortcuts = useRegisteredShortcuts();

  useCommandPaletteDialogShortcuts(() => {
    setIsOpen((prev) => !prev);
  });

  const shortcutCommands: CommandOption[] = useMemo(() => {
    return shortcuts
      .filter(
        (shortcut) =>
          // prevent the command palette from showing up in the list
          shortcut.description !== t('keyboardShortcuts.openCommandPalette'),
      )
      .sort((a, b) => {
        // sort by the grouping
        if (a.category === b.category) {
          return a.description.localeCompare(b.description);
        }
        return (
          categoryOrder.indexOf(a.category) - categoryOrder.indexOf(b.category)
        );
      })
      .map((shortcut) => {
        return {
          groupBy: t(`keyboardShortcuts.categories.${shortcut.category}`),
          label: shortcut.description,
          subtitle: shortcut.subtitle,
          handler: shortcut.handler,
          render: (props, key) => (
            <ListItemButton component="li" key={key} {...props}>
              {/* Space is reserved on every row so labels stay aligned whether or not a dot shows */}
              <Box
                sx={{
                  width: 16,
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {shortcut.isActive && <Badge variant="dot" color="success" />}
              </Box>
              <ListItemText
                primary={shortcut.description}
                secondary={shortcut.subtitle}
              />
              {shortcut.keys && (
                <Chip label={shortcut.keys} variant="keyboard-shortcut" />
              )}
            </ListItemButton>
          ),
        };
      });
  }, [shortcuts, t]);

  const handleSelection = (command: CommandOption | null): void => {
    if (command) {
      setIsOpen(false);
      command.handler();
    }
  };

  return (
    <ForcedTheme mode="dark">
      <Dialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        maxWidth="sm"
        disablePortal
        slotProps={{
          paper: {
            style: { overflow: 'visible' },
          },
        }}
        sx={{
          // Because autocomplete takes up room, move dialog to top ~1/4 of screen
          marginTop: 20,
          '& .MuiDialog-container': {
            alignItems: 'flex-start',
          },
        }}
        fullWidth
      >
        {shortcutCommands.length > 0 && (
          <DialogContent sx={{ overflow: 'visible' }}>
            <Autocomplete
              fullWidth
              autoHighlight
              openOnFocus
              disablePortal
              options={shortcutCommands}
              clearOnEscape
              filterOptions={filterCommandOptions}
              onChange={(_event, command) => handleSelection(command)}
              getOptionLabel={(command) => command.label}
              groupBy={(command) => command.groupBy}
              renderInput={(params: AutocompleteRenderInputParams) => (
                <TextField
                  {...params}
                  inputRef={inputRef}
                  onKeyDown={(e) => e.key === 'Escape' && setIsOpen(false)}
                />
              )}
              renderOption={({ key, ...props }, option) =>
                option.render(props, key)
              }
            />
          </DialogContent>
        )}
      </Dialog>
    </ForcedTheme>
  );
};
